import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import { planStatusLabel } from './planConstants';

const TENANT_STATUSES = ['pending', 'trial', 'active', 'suspended', 'archived'];

export default function PlatformTenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = usePlatformAuth();
  const isSuper = user?.role === 'super_admin';

  const [tenant, setTenant] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/tenants/${id}/overview`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Not found');
      setTenant(json.tenant);
      setOverview(json.overview);
    } catch (e) {
      setError(e.message);
      setTenant(null);
      setOverview(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const postStatus = async (status) => {
    setBusy(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/tenants/${id}/status`, {
        method: 'POST',
        body: { status }
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Update failed');
      setTenant(json.tenant);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !tenant) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!tenant) return null;

  const row = (label, value) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1">{value ?? '—'}</Typography>
    </Grid>
  );

  const card = (title, value) => (
    <Grid item xs={6} sm={4} md={2}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h6">{value}</Typography>
      </Paper>
    </Grid>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h4">{tenant.tenant_name}</Typography>
          <Typography variant="body2" color="text.secondary">
            <code>{tenant.tenant_code}</code>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/platform/tenants')}>
            Back to list
          </Button>
          {(user?.role === 'super_admin' || user?.role === 'analyst') && (
            <Button variant="outlined" onClick={() => navigate(`/platform/tenants/${id}/tenant-settings`)}>
              Tenant settings
            </Button>
          )}
          {isSuper && (
            <Button variant="contained" onClick={() => navigate(`/platform/tenants/${id}/edit`)}>
              Edit
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {overview && (
        <>
          <Typography variant="h6" gutterBottom>
            Overview
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {card('Users', overview.user_count)}
            {card('Active users', overview.active_user_count)}
            {card('Roles', overview.role_count)}
            {card('Audit rows', overview.audit_event_count)}
            {card('API reqs (30d)', overview.last_30d_api_requests)}
            {card('Login rollups (30d)', overview.last_30d_auth_login_rollups)}
          </Grid>
        </>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip label={planStatusLabel(tenant.status)} />
        </Box>
        {isSuper && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {TENANT_STATUSES.map((s) => (
              <Button key={s} size="small" disabled={busy || tenant.status === s} onClick={() => postStatus(s)}>
                Set {planStatusLabel(s)}
              </Button>
            ))}
          </Box>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Profile
        </Typography>
        <Grid container spacing={2}>
          {row('Plan', tenant.plan_name ? `${tenant.plan_name} (${tenant.plan_code})` : '—')}
          {row('Primary contact', tenant.primary_contact_name)}
          {row('Contact email', tenant.primary_contact_email)}
          {row('Contact phone', tenant.primary_contact_phone)}
          {row('Domain', tenant.domain)}
          {row('Subdomain', tenant.subdomain)}
          {row('Timezone', tenant.timezone)}
          {row('Country', tenant.country)}
          {row('Billing email', tenant.billing_email)}
          {row('Trial ends', tenant.trial_ends_at ? String(tenant.trial_ends_at) : '—')}
          {row('Activated', tenant.activated_at ? String(tenant.activated_at) : '—')}
          {row('Suspended', tenant.suspended_at ? String(tenant.suspended_at) : '—')}
          {row('Archived', tenant.archived_at ? String(tenant.archived_at) : '—')}
          {row('Created', tenant.created_at ? String(tenant.created_at) : '—')}
          {row('Updated', tenant.updated_at ? String(tenant.updated_at) : '—')}
        </Grid>
      </Paper>
    </Box>
  );
}
